import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Mic, 
  MicOff, 
  Paperclip, 
  Calendar, 
  MessageSquare, 
  FileText,
  Search,
  Settings,
  Bot
} from 'lucide-react';
import ChatMessage from './ChatMessage';
import VoiceRecorder from './VoiceRecorder';
import TypingIndicator from './TypingIndicator';
import BackgroundScene from './BackgroundScene';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type?: 'text' | 'voice' | 'file';
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hey there! I'm Sair, your AI companion. I'm here to help you stay organized, productive, and ahead of the game. Whether you need to schedule meetings, set reminders, manage projects, or dive into research - I've got your back! What would you like to tackle today?",
      sender: 'ai',
      timestamp: new Date(),
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isTemplateActive, setIsTemplateActive] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ isDown: boolean; startX: number; scrollLeft: number; pointerId?: number }>({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
  });
  const isFirstRenderRef = useRef(true);

  // Debug utility function
  const debugResponse = (data: any, source: string) => {
    console.log(`=== ${source} Response Debug ===`);
    console.log('Session ID:', sessionId);
    console.log('Raw response:', data);
    console.log('Response type:', typeof data);
    if (data && typeof data === 'object') {
      console.log('Response keys:', Object.keys(data));
      Object.entries(data).forEach(([key, value]) => {
        console.log(`${key}:`, value, `(type: ${typeof value})`);
      });
    }
    console.log('========================');
  };

  // Helper function to extract response text from n8n webhook response
  const extractResponseText = (data: any): string => {
    // If data is a string, check if it's JSON and format it properly
    if (typeof data === 'string') {
      const trimmed = data.trim();
      
      // Check if it's a JSON string and try to parse and format it
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          return formatJsonToMarkdown(parsed);
        } catch (e) {
          // If parsing fails, return as regular string
          return trimmed;
        }
      }
      
      return trimmed;
    }

    // If data is not an object, try to convert to string
    if (data === null || data === undefined) {
      return '';
    }

    if (typeof data !== 'object') {
      return String(data).trim();
    }

    // Handle array responses (like the one you're getting)
    if (Array.isArray(data)) {
      if (data.length === 0) {
        return '';
      }
      
      // If it's an array of objects, try to extract from the first object
      if (typeof data[0] === 'object') {
        const firstItem = data[0];
        // Check for common response fields in the first array item
        const possibleFields = [
          'output', 'response', 'message', 'text', 'content', 'answer', 'reply',
          'result', 'data', 'body', 'response_text', 'ai_response'
        ];
        
        for (const field of possibleFields) {
          if (firstItem[field] && typeof firstItem[field] === 'string' && firstItem[field].trim()) {
            return firstItem[field].trim();
          }
        }
        
        // If no common fields found, try to find any string field in the first item
        const stringFields = Object.entries(firstItem)
          .filter(([key, value]) => typeof value === 'string' && value.trim().length > 0)
          .map(([key, value]) => ({ key, value: value as string }));
        
        if (stringFields.length > 0) {
          console.log('Found string fields in first array item:', stringFields);
          return stringFields[0].value.trim();
        }
        
        // If still no string found, try to stringify the first item
        console.log('No string fields found, stringifying first item:', firstItem);
        return JSON.stringify(firstItem).trim();
      }
      
      // If it's an array of strings, return the first one
      if (typeof data[0] === 'string') {
        return data[0].trim();
      }
      
      // If it's an array of other types, try to convert to string
      return String(data[0]).trim();
    }

    // Handle object responses
    const possibleFields = [
      'output', 'response', 'message', 'text', 'content', 'answer', 'reply',
      'result', 'data', 'body', 'response_text', 'ai_response'
    ];
    
    for (const field of possibleFields) {
      if (data[field] && typeof data[field] === 'string' && data[field].trim()) {
        return data[field].trim();
      }
    }
    
    // If no common fields found, try to find any string field
    const stringFields = Object.entries(data)
      .filter(([key, value]) => typeof value === 'string' && value.trim().length > 0)
      .map(([key, value]) => ({ key, value: value as string }));
    
    if (stringFields.length > 0) {
      console.log('Found string fields in object:', stringFields);
      return stringFields[0].value.trim();
    }
    
    // If still no string found, try to stringify the object
    console.log('No string fields found, stringifying object:', data);
    return JSON.stringify(data).trim();
  };

  // Helper function to convert JSON objects to formatted markdown
  const formatJsonToMarkdown = (obj: any): string => {
    let result = '';
    
    for (const [key, value] of Object.entries(obj)) {
      // Convert key to title case and bold it
      const title = key.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      result += `**${title}:** `;
      
      if (Array.isArray(value)) {
        // Handle arrays (like lists)
        result += '\n';
        value.forEach((item, index) => {
          result += `- ${item}\n`;
        });
      } else if (typeof value === 'string') {
        // Handle strings
        result += `${value}\n`;
      } else {
        // Handle other types
        result += `${JSON.stringify(value)}\n`;
      }
      
      result += '\n';
    }
    
    return result.trim();
  };

  // Smart formatting function
  const formatResponseText = (text: string): string => {
    // Check if text already has markdown formatting (like from JSON conversion)
    const hasExistingFormatting = /(\*\*.*\*\*)|(\*.*\*)|(^#+\s)|(^-\s)|(^\d+\.\s)/m.test(text);

    if (hasExistingFormatting) {
      // If it already has formatting, just clean it lightly
      return cleanExistingFormatting(text);
    }

    // Initial character cleaning for unformatted text
    let formatted = text
      .replace(/[{}]/g, '')
      .replace(/[\[\]]/g, '')
      .replace(/"/g, '')
      .replace(/_/g, ' ') // Replaced _ with space
      .replace(/\\n/g, '\n');

    formatted = smartFormatUnformattedText(formatted);
    formatted = applySmartCapitalization(formatted);

    return formatted;
  };

  // Smart formatting for unformatted text
  const smartFormatUnformattedText = (text: string): string => {
    let formatted = text;

    // Bold lines that start with ###
    formatted = formatted.replace(/^###\s*(.+)$/gm, '**$1**');

    // Bold headers (lines that end with colon or are short and all caps)
    formatted = formatted.replace(/^([A-Z][A-Z\s]+):$/gm, '**$1:**');
    formatted = formatted.replace(/^([A-Z][A-Z\s]{2,15})$/gm, '**$1**');

    // Bold key-value pairs (including JSON-like patterns)
    formatted = formatted.replace(/^([A-Za-z\s]+):\s*(.+)$/gm, '**$1:** $2');
    
    // Handle JSON-like patterns with quotes and special characters
    formatted = formatted.replace(/^([A-Za-z\s]+):\s*["']?([^"']+)["']?$/gm, '**$1:** $2');
    
    // Handle patterns like "name: value," (with trailing comma)
    formatted = formatted.replace(/^([A-Za-z\s]+):\s*([^,]+),?$/gm, '**$1:** $2');

    // Bold important terms (words that are likely important)
    const importantTerms = [
      'deadline', 'priority', 'status', 'assignee', 'project', 'task', 'meeting',
      'reminder', 'schedule', 'due date', 'completed', 'pending', 'in progress',
      'high', 'medium', 'low', 'urgent', 'important', 'critical', 'summary',
      'warriors', 'name', 'description', 'title', 'content', 'details'
    ];
    
    importantTerms.forEach(term => {
      const regex = new RegExp(`\\b${term}\\b`, 'gi');
      formatted = formatted.replace(regex, `**${term}**`);
    });

    // Bold numbers and percentages
    formatted = formatted.replace(/\b(\d+(?:\.\d+)?%?)\b/g, '**$1**');

    // Bold emails
    formatted = formatted.replace(/\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g, '**$1**');

    // Bold URLs
    formatted = formatted.replace(/\b(https?:\/\/[^\s]+)\b/g, '**$1**');

    // Bold phone numbers
    formatted = formatted.replace(/\b(\+?[\d\s\-\(\)]{10,})\b/g, '**$1**');

    return formatted;
  };

  // Clean existing formatting
  const cleanExistingFormatting = (text: string): string => {
    return text
      .replace(/[{}]/g, '')
      .replace(/[\[\]]/g, '')
      .replace(/"/g, '')
      .replace(/_/g, ' ')
      .replace(/\\n/g, '\n')
      .replace(/\*\*(.*?)\*\*/g, '**$1**') // Ensure proper bold formatting
      .replace(/\*(.*?)\*/g, '*$1*'); // Ensure proper italic formatting
  };

  // Smart capitalization
  const applySmartCapitalization = (text: string): string => {
    let formatted = text;

    // Capitalize first letter of sentences
    formatted = formatted.replace(/(^|\.\s+)([a-z])/g, (match, p1, p2) => p1 + p2.toUpperCase());

    // Capitalize proper nouns and important terms
    const properNouns = [
      'sair', 'ai', 'project', 'task', 'meeting', 'reminder', 'deadline',
      'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
      'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
      'september', 'october', 'november', 'december'
    ];

    properNouns.forEach(noun => {
      const regex = new RegExp(`\\b${noun}\\b`, 'gi');
      formatted = formatted.replace(regex, noun.charAt(0).toUpperCase() + noun.slice(1));
    });

    // Capitalize organization types
    formatted = formatted.replace(/\b(inc|corp|ltd|llc|co)\b/gi, match => match.toUpperCase());

    // Capitalize geographic terms
    formatted = formatted.replace(/\b(north|south|east|west|street|avenue|road|drive|lane)\b/gi, 
      match => match.charAt(0).toUpperCase() + match.slice(1));

    // Capitalize sports and business terms
    formatted = formatted.replace(/\b(ceo|cfo|cto|vp|hr|it|qa|ui|ux)\b/gi, match => match.toUpperCase());

    return formatted;
  };

  const testWebhookConnection = async () => {
    try {
      const conversationContext = messages
        .slice(-10)
        .map(msg => ({
          sender: msg.sender,
          text: msg.text,
          timestamp: msg.timestamp.toISOString(),
          type: msg.type || 'text'
        }));

      const response = await fetch('https://nyrix.app.n8n.cloud/webhook/a7810286-964f-49a8-9798-10540f2e6522/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: 'Test message from Sair AI Assistant',
          type: 'text',
          timestamp: new Date().toISOString(),
          userId: 'user',
          sessionId: sessionId,
          conversationContext: conversationContext,
          messageCount: messages.length + 1,
        }),
      });

      const data = await response.json();
      debugResponse(data, 'Test Webhook');
      
      if (response.ok) {
        console.log('✅ Webhook connection successful');
        alert('✅ Webhook connection successful! Check console for details.');
      } else {
        console.error('❌ Webhook connection failed:', response.status, response.statusText);
        alert('❌ Webhook connection failed! Check console for details.');
      }
    } catch (error) {
      console.error('❌ Webhook connection error:', error);
      alert('❌ Webhook connection error! Check console for details.');
    }
  };

  const testVoiceWebhookConnection = async () => {
    try {
      const conversationContext = messages
        .slice(-10)
        .map(msg => ({
          sender: msg.sender,
          text: msg.text,
          timestamp: msg.timestamp.toISOString(),
          type: msg.type || 'text'
        }));

      const response = await fetch('https://nyrix.app.n8n.cloud/webhook/a7810286-964f-49a8-9798-10540f2e6522/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: 'Test voice message from Sair AI Assistant',
          type: 'voice',
          timestamp: new Date().toISOString(),
          userId: 'user',
          sessionId: sessionId,
          conversationContext: conversationContext,
          messageCount: messages.length + 1,
        }),
      });

      const data = await response.json();
      debugResponse(data, 'Test Voice Webhook');
      
      if (response.ok) {
        console.log('✅ Voice webhook connection successful');
        alert('✅ Voice webhook connection successful! Check console for details.');
      } else {
        console.error('❌ Voice webhook connection failed:', response.status, response.statusText);
        alert('❌ Voice webhook connection failed! Check console for details.');
      }
    } catch (error) {
      console.error('❌ Voice webhook connection error:', error);
      alert('❌ Voice webhook connection error! Check console for details.');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Keep chat scrolled to latest message after initial paint
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    scrollToBottom();
  }, [messages]);

  // On initial load, ensure header is visible and input is focused without scrolling
  useEffect(() => {
    const focusAndPosition = () => {
      try {
        inputRef.current?.focus({ preventScroll: true } as any);
      } catch {
        inputRef.current?.focus();
      }
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {}
    };
    const id = window.setTimeout(focusAndPosition, 0);
    return () => window.clearTimeout(id);
  }, []);

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: text.trim(),
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTemplateActive(false);
    setIsTyping(true);

    // Prepare conversation context (last 10 messages)
    const conversationContext = messages
      .slice(-10)
      .map(msg => ({
        sender: msg.sender,
        text: msg.text,
        timestamp: msg.timestamp.toISOString(),
        type: msg.type || 'text'
      }));

    try {
      const response = await fetch('https://nyrix.app.n8n.cloud/webhook/a7810286-964f-49a8-9798-10540f2e6522/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text.trim(),
          type: 'text',
          timestamp: new Date().toISOString(),
          userId: 'user',
          sessionId: sessionId,
          conversationContext: conversationContext,
          messageCount: messages.length + 1,
        }),
      });

      const data = await response.json();
      debugResponse(data, 'Main Webhook');

      let responseText = extractResponseText(data);
      
      if (!responseText) {
        responseText = "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.";
      }

      // Format the response
      const formattedResponse = formatResponseText(responseText);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: formattedResponse,
        sender: 'ai',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, but I'm experiencing some technical difficulties right now. Please check your connection and try again.",
        sender: 'ai',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleVoiceInput = async (transcript: string) => {
    if (!transcript.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: transcript.trim(),
      sender: 'user',
      timestamp: new Date(),
      type: 'voice'
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    // Prepare conversation context (last 10 messages)
    const conversationContext = messages
      .slice(-10)
      .map(msg => ({
        sender: msg.sender,
        text: msg.text,
        timestamp: msg.timestamp.toISOString(),
        type: msg.type || 'text'
      }));

    try {
      const response = await fetch('https://nyrix.app.n8n.cloud/webhook/a7810286-964f-49a8-9798-10540f2e6522/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: transcript.trim(),
          type: 'message',
          timestamp: new Date().toISOString(),
          userId: 'user',
          sessionId: sessionId,
          conversationContext: conversationContext,
          messageCount: messages.length + 1,
        }),
      });

      const data = await response.json();
      debugResponse(data, 'Voice Webhook');

      let responseText = extractResponseText(data);
      
      if (!responseText) {
        responseText = "I apologize, but I'm having trouble processing your voice input right now. Please try again in a moment.";
      }

      // Format the response
      const formattedResponse = formatResponseText(responseText);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: formattedResponse,
        sender: 'ai',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error processing voice input:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, but I'm experiencing some technical difficulties with voice processing right now. Please try typing your message instead.",
        sender: 'ai',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputText);
    }
  };

  const quickActions = [
    { 
      icon: Calendar, 
      label: 'Schedule Meeting', 
      template: 'Schedule a meeting with [NAME] on [DATE] at [TIME] for [PURPOSE]' 
    },
    { 
      icon: MessageSquare, 
      label: 'Send Reminder', 
      template: 'Send me a reminder to [ACTION] on [DATE] at [TIME]' 
    },
    { 
      icon: FileText, 
      label: 'Create Project', 
      template: 'Create a new project called "[PROJECT_NAME]" for [DESCRIPTION] with deadline [DEADLINE]' 
    },
    { 
      icon: Search, 
      label: 'Research', 
      template: 'Research [TOPIC] in [LOCATION] and find [NUMBER] options for [SERVICE_TYPE]' 
    },
  ];

  const handleQuickAction = (template: string) => {
    setInputText(template);
    setIsTemplateActive(true);
    // Focus on the input field
    const textarea = inputRef.current;
    if (textarea) {
      textarea.focus();
      textarea.setSelectionRange(0, template.length);
    }
  };

  // Enable drag-to-scroll for quick actions
  const handleQuickActionsPointerDown = (e: React.PointerEvent) => {
    const container = quickActionsRef.current;
    if (!container) return;
    container.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      isDown: true,
      startX: e.clientX,
      scrollLeft: container.scrollLeft,
      pointerId: e.pointerId,
    };
  };

  const handleQuickActionsPointerMove = (e: React.PointerEvent) => {
    const container = quickActionsRef.current;
    if (!container) return;
    if (!dragStateRef.current.isDown) return;
    e.preventDefault();
    const walk = e.clientX - dragStateRef.current.startX;
    container.scrollLeft = dragStateRef.current.scrollLeft - walk;
  };

  const endQuickActionsDrag = (e?: React.PointerEvent) => {
    if (dragStateRef.current.pointerId !== undefined && quickActionsRef.current) {
      try { quickActionsRef.current.releasePointerCapture?.(dragStateRef.current.pointerId); } catch {}
    }
    dragStateRef.current.isDown = false;
  };

  return (
    <div className="h-full relative overflow-hidden">
      {/* Three.js Background */}
      <BackgroundScene />
      
      {/* Main Chat Interface */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-3 pt-6 pb-60 space-y-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Session ID Display */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center space-x-2 bg-modern-700/30 rounded-lg px-3 py-1.5 text-xs text-modern-300 border border-modern-600/50">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span>Session: {sessionId.substring(0, 20)}...</span>
                <button 
                  onClick={() => navigator.clipboard.writeText(sessionId)}
                  className="ml-2 text-modern-400 hover:text-modern-200 transition-colors"
                  title="Copy session ID"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <AnimatePresence>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChatMessage message={message} />
                </motion.div>
              ))}
            </AnimatePresence>
            
            {isTyping && <TypingIndicator context={inputText || messages[messages.length - 1]?.text} />}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Quick Actions */}
        <motion.div 
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="glass-effect p-2 border-t border-modern-700/50 fixed bottom-[82px] left-0 right-0 z-30"
        >
          <div className="max-w-6xl mx-auto">
            <div
              ref={quickActionsRef}
              className="flex space-x-2 overflow-x-auto no-scrollbar select-none cursor-grab active:cursor-grabbing scroll-smooth"
              onPointerDown={handleQuickActionsPointerDown}
              onPointerMove={handleQuickActionsPointerMove}
              onPointerUp={endQuickActionsDrag}
              onPointerLeave={endQuickActionsDrag}
            >
              {quickActions.map((action, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleQuickAction(action.template)}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-modern-700/50 hover:bg-modern-700/70 rounded-md text-white text-xs font-medium transition-all duration-200 hover:shadow-lg border border-modern-600/50 whitespace-nowrap backdrop-blur-sm"
                >
                  <action.icon className="w-3 h-3" />
                  <span>{action.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Input Area */}
        <motion.div 
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="glass-effect p-3 border-t border-modern-700/50 fixed bottom-0 left-0 right-0 z-40"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end space-x-4">
              <div className="flex-1 relative">
                {/* Template Active Indicator - Outside the textarea */}
                {isTemplateActive && (
                  <div className="mb-2 flex items-center justify-between bg-primary-100/90 border border-primary-200 rounded-lg px-3 py-2 backdrop-blur-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse shadow-lg"></div>
                      <span className="text-xs text-primary-800 font-semibold">Template Active</span>
                    </div>
                    <button 
                      onClick={() => {
                        setInputText('');
                        setIsTemplateActive(false);
                      }}
                      className="text-primary-700 hover:text-primary-900 transition-all duration-200 p-1 rounded hover:bg-primary-200/50"
                      title="Clear template"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                
                <textarea
                  ref={inputRef}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message or use voice input..."
                  className={`w-full p-3 pr-12 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder-modern-500 text-modern-800 font-medium shadow-lg border ${
                    isTemplateActive 
                      ? 'bg-primary-50/90 border-primary-300 text-primary-800' 
                      : 'bg-modern-100/95 border-modern-200'
                  }`}
                  rows={1}
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                  autoFocus
                />
                
                <div className="absolute right-4 bottom-4 flex items-center space-x-3">
                  <button className="text-modern-500 hover:text-modern-700 transition-colors p-2 rounded-lg hover:bg-modern-200/50">
                    <Paperclip className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <VoiceRecorder
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                onTranscript={handleVoiceInput}
              />
              
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSendMessage(inputText)}
                disabled={!inputText.trim()}
                className="p-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-modern-400 disabled:to-modern-500 rounded-xl text-white transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Send className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Chat; 